import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiArrowRight,
  FiDollarSign,
  FiHash,
  FiHome,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronRight,
  FiUser,
} from "react-icons/fi";

import {
  transferFund,
  getUserDetailsByUserId,
  resetTransferStatus,
  clearUserDetails,
} from "../../store/slices/fund";

import {
  getDashboardSummary,
} from "../../store/slices/profile";

const TransferFund = () => {
  const dispatch = useDispatch();

  const {
    transferLoading,
    transferSuccess,
    transferError,

    userDetails,
    userDetailsLoading,
    userDetailsError,
  } = useSelector((state) => state.fund);

  const {
    dashboardSummary,
    getDashboardSummaryLoading,
  } = useSelector((state) => state.profile);

  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");

  const [validationError, setValidationError] =
    useState("");

  useEffect(() => {
    dispatch(getDashboardSummary());
  }, [dispatch]);

  const currentBalance =
    dashboardSummary?.currentBalance;

  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
    setValidationError("");
  };

  useEffect(() => {
    const trimmedUserId = userId.trim();

    if (!trimmedUserId) {
      dispatch(clearUserDetails());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(
        getUserDetailsByUserId({
          userId: trimmedUserId,
        })
      );
    }, 600);

    return () => clearTimeout(timer);

  }, [userId, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setValidationError("");

    const trimmedUserId = userId.trim();
    const numericAmount = Number(amount);
    const availableBalance = Number(currentBalance || 0);
    if (!trimmedUserId) {
      setValidationError("Please enter User ID.");
      return;
    }

    if (userDetailsLoading) {
      setValidationError("Please wait while verifying the user.");
      return;
    }

    if (!recipientName) {
      setValidationError(
        "Please enter a valid User ID and verify the user."
      );
      return;
    }
    if (!amount || isNaN(numericAmount)) {
      setValidationError("Please enter a valid amount.");
      return;
    }

    if (numericAmount <= 0) {
      setValidationError("Amount must be greater than 0.");
      return;
    }

    if (numericAmount > availableBalance) {
      setValidationError(
        `Insufficient balance. Your current balance is $ ${availableBalance.toFixed(
          2
        )}.`
      );
      return;
    }

    try {
      const payload = {
        userId: trimmedUserId,
        amount: numericAmount,
      };

      console.log("TRANSFER FUND PAYLOAD:", payload);

      await dispatch(
        transferFund(payload)
      ).unwrap();

      dispatch(getDashboardSummary());

      dispatch(clearUserDetails());
      setUserId("");
      setAmount("");

    } catch (error) {
      console.error("TRANSFER FUND ERROR:", error);
    }
  };

  const handleReset = () => {
    setUserId("");
    setAmount("");
    setValidationError("");

    dispatch(
      resetTransferStatus()
    );

    dispatch(
      clearUserDetails()
    );

    dispatch(
      getDashboardSummary()
    );
  };

  const recipientName =
    userDetails?.name ||
    userDetails?.user?.name ||
    userDetails?.data?.name ||
    "";

  return (
    <div className="gi-transfer-page">

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Fund Transfer
        </span>
        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Transfer Fund
        </span>

      </div>

      <section className="gi-transfer-card">


        <div className="gi-current-balance">

          <div>

            <span>
              Current Balance
            </span>

            <strong>

              {getDashboardSummaryLoading
                ? "Loading..."
                : ` $ ${Number(currentBalance).toFixed(2)}`
              }

            </strong>

          </div>

        </div>


        {transferSuccess && (

          <div className="gi-transfer-success">

            <FiCheckCircle />

            <div>

              <strong>
                Fund transferred successfully
              </strong>

              <p>
                The fund has been transferred successfully.
              </p>

            </div>

          </div>

        )}

        {(validationError || transferError) && (

          <div className="gi-transfer-error">

            <FiAlertCircle />

            <span>
              {validationError || transferError}
            </span>

          </div>

        )}

        {!transferSuccess && (

          <form
            onSubmit={handleSubmit}
            className="gi-transfer-form"
          >

            <div className="gi-transfer-form-group">

              <label htmlFor="userId">

                User ID

                <span>*</span>

              </label>

              <div className="gi-transfer-input-wrapper">

                <FiHash />

                <input
                  id="userId"
                  type="text"
                  value={userId}
                  onChange={handleUserIdChange}

                  placeholder="Enter recipient User ID"
                  className="gi-transfer-input"
                  disabled={transferLoading} maxLength={10}
                  autoComplete="off"
                />

                {userDetailsLoading && (
                  <FiLoader className="gi-transfer-spinner" />
                )}

              </div>

              <small>
                Enter the User ID of the member you want to transfer funds to.
              </small>

              {userDetailsError && userId && (

                <div className="gi-user-not-found" style={{ color: "red" }}>

                  <FiAlertCircle />&nbsp;

                  {userDetailsError}

                </div>

              )}

            </div>

            {recipientName && (

              <div className="gi-transfer-form-group">

                <label htmlFor="userName">

                  User Name

                </label>

                <div className="gi-transfer-input-wrapper gi-user-name-field">

                  <FiUser />

                  <input
                    id="userName"
                    type="text"
                    value={recipientName}
                    readOnly
                    className="gi-transfer-input"
                  />

                  <FiCheckCircle
                    className="gi-user-verified-icon"
                  />

                </div>

                <small className="gi-user-found">

                  User verified successfully

                </small>

              </div>

            )}


            <div className="gi-transfer-form-group">

              <label htmlFor="amount">

                Amount

                <span>*</span>

              </label>

              <div className="gi-transfer-input-wrapper">

                <FiDollarSign />

                <input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;


                    if (!/^\d*\.?\d{0,2}$/.test(value)) {
                      return;
                    }

                    if (value.length > 4) {
                      return;
                    }

                    setAmount(value);

                    const numericValue = Number(value);
                    const availableBalance = Number(currentBalance || 0);

                    if (value && numericValue > availableBalance) {
                      setValidationError(
                        `Amount cannot be greater than your current balance ($ ${availableBalance.toFixed(2)}).`
                      );
                    } else {
                      setValidationError("");
                    }
                  }}
                  placeholder="Enter amount"
                  min="0.01"
                  max={Number(currentBalance || 0)}
                  step="0.01"
                  className="gi-transfer-input"
                  disabled={transferLoading}
                />

              </div>

              <small>
                Available balance: $ {Number(currentBalance).toFixed(2)}
              </small>

            </div>

            <button
              type="submit"
              className="gi-transfer-submit"
              disabled={
                transferLoading ||
                userDetailsLoading ||
                !recipientName ||
                !amount ||
                Number(amount) <= 0 ||
                Number(amount) > Number(currentBalance || 0) ||
                !!validationError
              }
            >
              {transferLoading ? (
                <>
                  <FiLoader className="gi-transfer-spinner" />
                  Transferring...
                </>
              ) : (
                <>
                  Transfer Fund
                  <FiArrowRight />
                </>
              )}
            </button>

          </form>

        )}

        {transferSuccess && (

          <div className="gi-transfer-success-actions">

            <button
              type="button"
              onClick={handleReset}
              className="gi-transfer-new-btn"
            >

              Make Another Transfer

            </button>

          </div>

        )}

      </section>

    </div>
  );
};

export default TransferFund;